import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { AdminLoginResponse, ApiError, ApiErrorResponse } from '../types/api';
import { getApiBaseUrl } from '../config/api';

// LocalStorage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'cent_admin_access_token',
  USER: 'cent_admin_user',
};

export const DUMMY_AUTH = {
  accessToken: 'dummy-access-token',
  credentials: [
    {
      email: 'dummy@cent.com',
      password: 'dummy123',
      user: {
        id: 'dummy-user-1',
        email: 'dummy@cent.com',
        role: 'admin' as const,
      },
    },
    {
      email: 'admin@cent.com',
      password: 'admin123',
      user: {
        id: 'dummy-user-2',
        email: 'admin@cent.com',
        role: 'admin' as const,
      },
    },
    {
      email: 'admin123',
      password: 'admin123',
      user: {
        id: 'dummy-user-3',
        email: 'admin@cent.com',
        role: 'admin' as const,
      },
    },
  ],
};

interface AuthContextType {
  accessToken: string | null;
  isAuthenticated: boolean;
  user: AdminLoginResponse['user'] | null;
  login: (email: string, password: string) => Promise<AdminLoginResponse>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize state from localStorage
  const [accessToken, setAccessToken] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    }
    return null;
  });
  
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (typeof window !== 'undefined') {
      return !!localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    }
    return false;
  });
  
  const [user, setUser] = useState<AdminLoginResponse['user'] | null>(() => {
    if (typeof window !== 'undefined') {
      const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
      if (storedUser) {
        try {
          return JSON.parse(storedUser);
        } catch (e) {
          console.error('Error parsing stored user:', e);
          return null;
        }
      }
    }
    return null;
  });

  // Sync state with localStorage
  useEffect(() => {
    if (accessToken) {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
      setIsAuthenticated(true);
      console.log('✅ Token synced to localStorage, length:', accessToken.length);
    } else {
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      setIsAuthenticated(false);
      console.log('🔓 Token removed from localStorage');
    }
  }, [accessToken]);
  
  // Verify token is loaded on mount
  useEffect(() => {
    const storedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (storedToken && !accessToken) {
      console.log('🔄 Restoring token from localStorage on mount');
      setAccessToken(storedToken);
    }
  }, [accessToken]); // Only restores if a token exists and state is empty

  useEffect(() => {
    if (user) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    } else {
      localStorage.removeItem(STORAGE_KEYS.USER);
    }
  }, [user]);

  const login = useCallback(async (email: string, password: string): Promise<AdminLoginResponse> => {
    const url = `${getApiBaseUrl()}/admin/login`;
    const requestBody = { email, password };
    
    console.log('🔐 Attempting login to:', url);
    console.log('📧 Credentials:', { email, passwordLength: password.length });
    console.log('📦 Request body:', requestBody);
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestBody),
        credentials: 'include',
      });

      console.log('📡 Login response status:', response.status);
      console.log('📡 Login response headers:', Object.fromEntries(response.headers.entries()));

      // Try to parse error response
      let errorData: ApiErrorResponse | null = null;
      if (!response.ok) {
        try {
          const errorText = await response.text();
          console.error('❌ Error response body:', errorText);
          errorData = JSON.parse(errorText) as ApiErrorResponse;
        } catch (parseError) {
          console.error('❌ Could not parse error response');
          errorData = {
            message: `HTTP ${response.status}: ${response.statusText}`,
            statusCode: response.status,
            error: 'UNKNOWN_ERROR'
          };
        }
        
        console.error('❌ Login failed:', errorData);
        throw new ApiError(
          errorData.message || 'Login failed',
          errorData.statusCode || response.status,
          errorData.error || 'LOGIN_ERROR'
        );
      }

      const headerToken = response.headers.get('Authorization') || response.headers.get('authorization');
      let parsedToken = headerToken?.replace(/^Bearer\s+/i, '') ?? null;
      let responseData: AdminLoginResponse | null = null;

      try {
        responseData = (await response.json()) as AdminLoginResponse;
      } catch (parseError) {
        console.warn('⚠️ Unable to parse login response body:', parseError);
      }

      const possibleToken =
        responseData?.accessToken ??
        (responseData as any)?.token ??
        (responseData as any)?.jwt ??
        (responseData as any)?.access_token ??
        null;

      if (!parsedToken && typeof possibleToken === 'string' && possibleToken.length > 0) {
        parsedToken = possibleToken;
      }

      if (parsedToken) {
        console.log('✅ Login successful, received token header/body.');
      } else {
        console.log('✅ Login successful without explicit access token (assuming session cookies).');
      }

      const finalUser = responseData?.user ?? {
        id: '',
        email,
        role: 'admin' as const,
      };

      if (responseData?.user) {
        console.log('👤 User data:', responseData.user);
      }

      setAccessToken(parsedToken);
      setIsAuthenticated(true);
      setUser(finalUser);
      
      // Verify token was saved to localStorage
      const savedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      console.log('🔑 Token stored:', {
        hasToken: !!parsedToken,
        tokenLength: parsedToken?.length,
        savedToStorage: !!savedToken,
        savedTokenLength: savedToken?.length,
        tokensMatch: parsedToken === savedToken
      });
      
      if (!savedToken && parsedToken) {
        console.error('❌ CRITICAL: Token was set but not saved to localStorage!');
      }
      
      return {
        accessToken: parsedToken ?? '',
        user: finalUser,
      };
    } catch (error) {
      console.error('💥 Login error:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    console.log('🚪 Logging out...');
    setAccessToken(null);
    setIsAuthenticated(false);
    setUser(null);
    // Clear localStorage
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
  }, []);

  const value: AuthContextType = {
    accessToken,
    isAuthenticated,
    user,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
